import { useCallback, useRef, useState } from 'react'
import { IconButton, Stack, TextField } from '@mui/material'
import { replyBoxFieldSx } from './messageColors.js'
import { ExpandIcon } from './icons.jsx'
import PlaceholderOverlay from './PlaceholderOverlay.jsx'

// Shared expand/collapse control for multi-line text boxes (order notes,
// drafted replies, template/rule bodies, etc.) across the app — previously
// hand-duplicated at each call site (a local `expanded` boolean + IconButton
// + minRows/maxRows ternary, one of which — SpamRuleDialog.jsx — had even
// drifted into its own local ExpandIcon copy and a two-branch JSX layout
// instead of one field with a ternary). Manages its own `expanded` state
// internally — none of today's call sites need it controlled from outside.
//
// minRows/maxRows behave exactly like the plain TextField props they are —
// whatever a call site already passes for its COLLAPSED sizing keeps working
// unchanged. expandedMinRows/expandedMaxRows (default 20/40, matching the
// convention every existing expand toggle already used) apply only once
// expanded. Every other TextField prop (value, onChange, label, disabled,
// fullWidth, size, multiline, sx, etc.) passes straight through to the
// underlying TextField unchanged — `sx` in particular targets the TextField
// itself, same as any ordinary TextField usage; use containerSx for the rare
// site that needs the surrounding Stack's own flex sizing instead (e.g. two
// side-by-side textareas sharing a row unevenly).
//
// isCustomerReply (2026-10-08): this box's text is sent to the customer (a
// drafted reply, a message-to-buyer step, the closed-conversation composer),
// so it takes the reply-box fill in dark mode (messageColors.js). Never set
// on a note box.
//
// highlightPlaceholders (2026-10-26, on by default for a customer reply): an
// unfilled placeholder (an INSERT token, an XXX) is highlighted, as in the
// email editor, by a layer drawn over the textarea (PlaceholderOverlay.jsx).
// Display only: the value, and so what is sent, is untouched.
export default function ExpandableTextField({ minRows = 3, maxRows, expandedMinRows = 20, expandedMaxRows = 40, containerSx, isCustomerReply = false, highlightPlaceholders = isCustomerReply, sx, ...textFieldProps }) {
  const [expanded, setExpanded] = useState(false)
  const fieldSx = isCustomerReply ? [(theme) => replyBoxFieldSx(theme), ...(Array.isArray(sx) ? sx : [sx])] : sx
  const containerRef = useRef(null)
  const textareaRef = useRef(null)
  const { inputRef: callerInputRef } = textFieldProps
  // The caller's own inputRef (a function or a ref object) still gets the
  // element; the highlight keeps its own reference too.
  const inputRef = useCallback((el) => {
    textareaRef.current = el
    if (typeof callerInputRef === 'function') callerInputRef(el)
    else if (callerInputRef) callerInputRef.current = el
  }, [callerInputRef])
  const highlight = highlightPlaceholders && textFieldProps.multiline
  return (
    <Stack ref={containerRef} direction="row" spacing={0.5} alignItems="flex-start" sx={{ flex: 1, alignSelf: 'stretch', minWidth: 0, ...(highlight ? { position: 'relative' } : {}), ...containerSx }}>
      <TextField
        {...textFieldProps}
        inputRef={highlight ? inputRef : callerInputRef}
        sx={fieldSx}
        inputProps={isCustomerReply ? { 'data-reply-box': 'true', ...(textFieldProps.inputProps ?? {}) } : textFieldProps.inputProps}
        minRows={expanded ? expandedMinRows : minRows}
        maxRows={expanded ? expandedMaxRows : maxRows}
      />
      <IconButton
        size="small"
        onClick={() => setExpanded((e) => !e)}
        title={expanded ? 'Collapse' : 'Expand'}
        sx={{ flexShrink: 0, mt: 0.5 }}
      >
        <ExpandIcon expanded={expanded} />
      </IconButton>
      {highlight && <PlaceholderOverlay value={textFieldProps.value} textareaRef={textareaRef} containerRef={containerRef} />}
    </Stack>
  )
}
